import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { 
  Calendar, LogOut, Users, BarChart3, Activity, Search, ShieldCheck, 
  ShieldAlert, RefreshCw, UserPlus, CreditCard, CheckCircle2, XCircle, 
  Clock, Edit3, Trash2, Eye, Filter, Server, DollarSign, Building2, Stethoscope, Phone, Mail, MapPin, Award
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/utils';


const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_BASE}/api`;

export default function AdminDashboard() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [subscriptionsData, setSubscriptionsData] = useState({ payment_orders: [], paid_subscribers: [] });
  const [systemStatus, setSystemStatus] = useState(null);

  const [activeView, setActiveView] = useState('stats'); // 'stats' | 'users' | 'appointments' | 'subscriptions' | 'settings'
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters & Search
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [userSearch, setUserSearch] = useState('');
  const [unverifiedOnly, setUnverifiedOnly] = useState(false);

  const [apptStatusFilter, setApptStatusFilter] = useState('all');
  const [apptSearch, setApptSearch] = useState('');

  // Modals state
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const [isUserFormOpen, setIsUserFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    role: 'doctor',
    specialization: '',
    clinic_name: '',
    clinic_address: '',
    city: '',
    company_name: '',
    division: '',
  });

  const [isGrantSubOpen, setIsGrantSubOpen] = useState(false);
  const [grantSubUser, setGrantSubUser] = useState(null);
  const [grantSubPlan, setGrantSubPlan] = useState('monthly');
  const [grantSubDays, setGrantSubDays] = useState(30);

  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
  const [rescheduleAppt, setRescheduleAppt] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(res.data);
    } catch (error) {
      toast.error('Failed to fetch platform statistics');
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      const params = {};
      if (userRoleFilter !== 'all') params.role = userRoleFilter;
      if (userSearch) params.search = userSearch;
      if (unverifiedOnly) params.verified = false;

      const res = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setUsers(res.data);
    } catch (error) {
      console.error('Fetch users error:', error);
    }
  }, [token, userRoleFilter, userSearch, unverifiedOnly]);

  const fetchAppointments = useCallback(async () => {
    try {
      const params = {};
      if (apptStatusFilter !== 'all') params.status = apptStatusFilter;
      if (apptSearch) params.search = apptSearch;

      const res = await axios.get(`${API}/admin/appointments`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setAppointments(res.data);
    } catch (error) {
      console.error('Fetch appointments error:', error);
    }
  }, [token, apptStatusFilter, apptSearch]);

  const fetchSubscriptions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubscriptionsData(res.data);
    } catch (error) {
      console.error('Fetch subscriptions error:', error);
    }
  }, [token]);

  const fetchSystemStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/system-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSystemStatus(res.data);
    } catch (error) {
      console.error('Fetch system status error:', error);
    }
  }, [token]);

  const fetchAllData = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        fetchStats(),
        fetchUsers(),
        fetchAppointments(),
        fetchSubscriptions(),
        fetchSystemStatus(),
      ]);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [fetchStats, fetchUsers, fetchAppointments, fetchSubscriptions, fetchSystemStatus]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // User Actions
  const handleToggleVerify = async (userId, currentStatus) => {
    try {
      const res = await axios.patch(
        `${API}/admin/users/${userId}/verify`,
        { verified: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || 'Verification updated');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update verification status');
    }
  };

  const handleDeleteUser = async (userObj) => {
    if (!window.confirm(`Are you sure you want to delete user "${userObj.full_name}" (${userObj.role.toUpperCase()})? This will also remove associated records.`)) {
      return;
    }
    try {
      const res = await axios.delete(`${API}/admin/users/${userObj.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data.message || 'User deleted');
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete user'));
    }
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Update user
        await axios.patch(`${API}/admin/users/${editingUser.id}`, userFormData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('User updated successfully');
      } else {
        // Create user
        await axios.post(`${API}/admin/users`, userFormData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('New user created successfully');
      }
      setIsUserFormOpen(false);
      setEditingUser(null);
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Operation failed'));
    }
  };

  const handleGrantSubscription = async (e) => {
    e.preventDefault();
    if (!grantSubUser) return;
    try {
      const res = await axios.post(
        `${API}/admin/users/${grantSubUser.id}/subscription`,
        { plan: grantSubPlan, days: parseInt(grantSubDays) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || 'Subscription granted');
      setIsGrantSubOpen(false);
      setGrantSubUser(null);
      fetchUsers();
      fetchSubscriptions();
      fetchStats();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to grant subscription'));
    }
  };

  // Appointment Actions
  const handleUpdateApptStatus = async (apptId, status) => {
    try {
      await axios.patch(
        `${API}/admin/appointments/${apptId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Appointment status updated to ${status.toUpperCase()}`);
      fetchAppointments();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update appointment status');
    }
  };

  const handleRescheduleSubmit = async (e) => {
    e.preventDefault();
    if (!rescheduleAppt || !rescheduleDate || !rescheduleTime) {
      toast.error('Please enter both date and time slot');
      return;
    }
    try {
      await axios.patch(
        `${API}/admin/appointments/${rescheduleAppt.id}`,
        {
          status: 'rescheduled',
          appointment_date: rescheduleDate,
          time_slot: rescheduleTime,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Appointment rescheduled successfully');
      setIsRescheduleOpen(false);
      setRescheduleAppt(null);
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to reschedule appointment');
    }
  };

  const handleDeleteAppointment = async (apptId) => {
    if (!window.confirm('Are you sure you want to delete this appointment?')) return;
    try {
      await axios.delete(`${API}/admin/appointments/${apptId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Appointment deleted');
      fetchAppointments();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete appointment');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Trigger search filters dynamically
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 text-white">
        <div className="animate-spin rounded-full h-14 w-14 border-4 border-primary border-t-transparent mb-4"></div>
        <p className="font-medium text-slate-300">Loading MRcall Admin Portal...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      {/* Top Admin Header */}
      <header className="bg-slate-900/90 backdrop-blur border-b border-slate-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="h-11 w-11 bg-gradient-to-tr from-indigo-600 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20">
              <ShieldCheck className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold bg-gradient-to-r from-white via-slate-200 to-slate-400 bg-clip-text text-transparent">
                  MRcall Admin Portal
                </h1>
                <Badge className="bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 text-xs uppercase px-2 py-0.5">
                  Super Admin
                </Badge>
              </div>
              <p className="text-xs text-slate-400">System Oversight & Control Center • Logged in as {user?.full_name}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchAllData}
              disabled={refreshing}
              className="bg-slate-800 hover:bg-slate-700 text-slate-200 border-slate-700"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh Data
            </Button>
            <Button
              onClick={handleLogout}
              variant="destructive"
              size="sm"
              className="bg-rose-600/20 hover:bg-rose-600/30 text-rose-300 border border-rose-500/30"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Navigation Tabs */}
      <nav className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 overflow-x-auto scrollbar-none py-2">
            <button
              onClick={() => setActiveView('stats')}
              className={`flex items-center px-4 py-2.5 rounded-lg font-medium text-sm transition-all whitespace-nowrap ${
                activeView === 'stats'
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Statistics & Analytics
            </button>

            <button
              onClick={() => setActiveView('users')}
              className={`flex items-center px-4 py-2.5 rounded-lg font-medium text-sm transition-all whitespace-nowrap ${
                activeView === 'users'
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              <Users className="h-4 w-4 mr-2" />
              User Management ({stats?.total_users || 0})
            </button>

            <button
              onClick={() => setActiveView('appointments')}
              className={`flex items-center px-4 py-2.5 rounded-lg font-medium text-sm transition-all whitespace-nowrap ${
                activeView === 'appointments'
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              <Activity className="h-4 w-4 mr-2" />
              Appointments ({stats?.total_appointments || 0})
            </button>

            <button
              onClick={() => setActiveView('subscriptions')}
              className={`flex items-center px-4 py-2.5 rounded-lg font-medium text-sm transition-all whitespace-nowrap ${
                activeView === 'subscriptions'
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Subscriptions & Revenue (₹{stats?.total_revenue_inr || 0})
            </button>

            <button
              onClick={() => setActiveView('settings')}
              className={`flex items-center px-4 py-2.5 rounded-lg font-medium text-sm transition-all whitespace-nowrap ${
                activeView === 'settings'
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              <Server className="h-4 w-4 mr-2" />
              System Diagnostics
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content Area */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1 w-full">
        {/* VIEW 1: STATISTICS & ANALYTICS */}
        {activeView === 'stats' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 shadow-sm hover:border-slate-700 transition-colors">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Total Doctors</p>
                    <h3 className="text-3xl font-bold text-white mt-1">{stats?.total_doctors || 0}</h3>
                  </div>
                  <div className="h-12 w-12 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center justify-center">
                    <Stethoscope className="h-6 w-6 text-emerald-400" />
                  </div>
                </div>
                <p className="text-xs text-slate-400">Registered Healthcare Providers</p>
              </div>

              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 shadow-sm hover:border-slate-700 transition-colors">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Total MRs</p>
                    <h3 className="text-3xl font-bold text-white mt-1">{stats?.total_mrs || 0}</h3>
                  </div>
                  <div className="h-12 w-12 bg-cyan-500/10 border border-cyan-500/20 rounded-xl flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-cyan-400" />
                  </div>
                </div>
                <p className="text-xs text-slate-400">Medical Representatives</p>
              </div>

              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 shadow-sm hover:border-slate-700 transition-colors">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Total Appointments</p>
                    <h3 className="text-3xl font-bold text-white mt-1">{stats?.total_appointments || 0}</h3>
                  </div>
                  <div className="h-12 w-12 bg-indigo-500/10 border border-indigo-500/20 rounded-xl flex items-center justify-center">
                    <Calendar className="h-6 w-6 text-indigo-400" />
                  </div>
                </div>
                <div className="flex gap-2 text-xs mt-1">
                  <span className="text-emerald-400">{stats?.approved_appointments || 0} Approved</span>
                  <span className="text-amber-400">• {stats?.pending_appointments || 0} Pending</span>
                </div>
              </div>

              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 shadow-sm hover:border-slate-700 transition-colors">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Platform Revenue</p>
                    <h3 className="text-3xl font-bold text-emerald-400 mt-1">₹{stats?.total_revenue_inr || 0}</h3>
                  </div>
                  <div className="h-12 w-12 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-emerald-400" />
                  </div>
                </div>
                <p className="text-xs text-slate-400">{stats?.completed_transactions || 0} Completed Payments</p>
              </div>
            </div>

            {/* Breakdown Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Appointments Status Breakdown */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Activity className="h-5 w-5 text-indigo-400" />
                  Appointments Breakdown
                </h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-300 flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full bg-emerald-500"></span> Approved
                      </span>
                      <span className="font-semibold text-slate-200">{stats?.approved_appointments || 0}</span>
                    </div>
                    <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden">
                      <div
                        className="bg-emerald-500 h-full rounded-full"
                        style={{
                          width: `${
                            stats?.total_appointments
                              ? (stats.approved_appointments / stats.total_appointments) * 100
                              : 0
                          }%`,
                        }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-300 flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full bg-amber-500"></span> Pending Approval
                      </span>
                      <span className="font-semibold text-slate-200">{stats?.pending_appointments || 0}</span>
                    </div>
                    <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden">
                      <div
                        className="bg-amber-500 h-full rounded-full"
                        style={{
                          width: `${
                            stats?.total_appointments
                              ? (stats.pending_appointments / stats.total_appointments) * 100
                              : 0
                          }%`,
                        }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-300 flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full bg-rose-500"></span> Cancelled
                      </span>
                      <span className="font-semibold text-slate-200">{stats?.cancelled_appointments || 0}</span>
                    </div>
                    <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden">
                      <div
                        className="bg-rose-500 h-full rounded-full"
                        style={{
                          width: `${
                            stats?.total_appointments
                              ? (stats.cancelled_appointments / stats.total_appointments) * 100
                              : 0
                          }%`,
                        }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-300 flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full bg-blue-500"></span> Rescheduled
                      </span>
                      <span className="font-semibold text-slate-200">{stats?.rescheduled_appointments || 0}</span>
                    </div>
                    <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden">
                      <div
                        className="bg-blue-500 h-full rounded-full"
                        style={{
                          width: `${
                            stats?.total_appointments
                              ? (stats.rescheduled_appointments / stats.total_appointments) * 100
                              : 0
                          }%`,
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Subscriptions Breakdown */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-cyan-400" />
                  MR Subscription Tiers
                </h3>
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className="bg-slate-800/80 border border-slate-700/50 rounded-lg p-3 text-center">
                    <p className="text-xs text-slate-400 font-medium">Free Plan</p>
                    <p className="text-2xl font-bold text-white mt-1">{stats?.subscription_counts?.free || 0}</p>
                    <p className="text-[10px] text-slate-500">5 appts/mo</p>
                  </div>
                  <div className="bg-cyan-950/40 border border-cyan-500/30 rounded-lg p-3 text-center">
                    <p className="text-xs text-cyan-300 font-medium">Monthly Plan</p>
                    <p className="text-2xl font-bold text-cyan-400 mt-1">{stats?.subscription_counts?.monthly || 0}</p>
                    <p className="text-[10px] text-cyan-500">₹199 / month</p>
                  </div>
                  <div className="bg-indigo-950/40 border border-indigo-500/30 rounded-lg p-3 text-center">
                    <p className="text-xs text-indigo-300 font-medium">Yearly Plan</p>
                    <p className="text-2xl font-bold text-indigo-400 mt-1">{stats?.subscription_counts?.yearly || 0}</p>
                    <p className="text-[10px] text-indigo-500">₹1599 / year</p>
                  </div>
                </div>

                <div className="bg-slate-800/50 rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-200">Active Paid Subscribers</p>
                    <p className="text-xs text-slate-400">Total MRs on Monthly or Yearly plans</p>
                  </div>
                  <Badge className="bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 text-lg px-3 py-1 font-bold">
                    {stats?.subscription_counts?.paid || 0}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* VIEW 2: USER MANAGEMENT */}
        {activeView === 'users' && (
          <div className="space-y-6">
            {/* Filter & Controls Bar */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                <div className="relative flex-1 md:w-72">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by name, email, city, company..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="pl-9 bg-slate-950 border-slate-800 text-slate-200 focus:border-indigo-500"
                  />
                </div>

                <div className="flex items-center bg-slate-950 border border-slate-800 rounded-lg p-1">
                  {['all', 'doctor', 'mr', 'admin'].map((role) => (
                    <button
                      key={role}
                      onClick={() => setUserRoleFilter(role)}
                      className={`px-3 py-1.5 rounded-md text-xs font-semibold uppercase transition-colors ${
                        userRoleFilter === role
                          ? 'bg-indigo-600 text-white'
                          : 'text-slate-400 hover:text-white'
                      }`}
                    >
                      {role}
                    </button>
                  ))}
                </div>

                <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer bg-slate-950 border border-slate-800 px-3 py-2 rounded-lg">
                  <input
                    type="checkbox"
                    checked={unverifiedOnly}
                    onChange={(e) => setUnverifiedOnly(e.target.checked)}
                    className="rounded border-slate-700 text-indigo-600 focus:ring-indigo-500 bg-slate-900"
                  />
                  Unverified Only
                </label>
              </div>

              <Button
                onClick={() => {
                  setEditingUser(null);
                  setUserFormData({
                    email: '',
                    password: '',
                    full_name: '',
                    phone: '',
                    role: 'doctor',
                    specialization: '',
                    clinic_name: '',
                    clinic_address: '',
                    city: '',
                    company_name: '',
                    division: '',
                  });
                  setIsUserFormOpen(true);
                }}
                className="bg-indigo-600 hover:bg-indigo-500 text-white font-medium shadow-md w-full md:w-auto"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add New User
              </Button>
            </div>

            {/* Users Table */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-950/80 border-b border-slate-800 text-slate-400 text-xs uppercase font-semibold">
                      <th className="px-6 py-4">User</th>
                      <th className="px-6 py-4">Role</th>
                      <th className="px-6 py-4">Details</th>
                      <th className="px-6 py-4">Location</th>
                      <th className="px-6 py-4">Status / Sub</th>
                      <th className="px-6 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800 text-sm">
                    {users.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center text-slate-500">
                          No users found matching the selected filters.
                        </td>
                      </tr>
                    ) : (
                      users.map((u) => (
                        <tr key={u.id} className="hover:bg-slate-800/50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center font-bold text-slate-200 overflow-hidden">
                                {u.doctor_photo || u.mr_photo ? (
                                  <img
                                    src={u.doctor_photo || u.mr_photo}
                                    alt={u.full_name}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  u.full_name?.charAt(0).toUpperCase() || 'U'
                                )}
                              </div>
                              <div>
                                <p className="font-semibold text-slate-100 flex items-center gap-1.5">
                                  {u.full_name}
                                  {u.verified ? (
                                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 inline" />
                                  ) : (
                                    <ShieldAlert className="h-3.5 w-3.5 text-amber-400 inline" />
                                  )}
                                </p>
                                <p className="text-xs text-slate-400">{u.email}</p>
                              </div>
                            </div>
                          </td>

                          <td className="px-6 py-4">
                            <Badge
                              className={`text-xs font-semibold px-2.5 py-0.5 uppercase border ${
                                u.role === 'doctor'
                                  ? 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30'
                                  : u.role === 'mr'
                                  ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30'
                                  : 'bg-purple-500/20 text-purple-300 border-purple-500/30'
                              }`}
                            >
                              {u.role}
                            </Badge>
                          </td>

                          <td className="px-6 py-4 text-xs text-slate-300">
                            {u.role === 'doctor' ? (
                              <div>
                                <p className="font-medium text-slate-200">{u.specialization || 'General'}</p>
                                <p className="text-slate-400">{u.clinic_name || '-'}</p>
                              </div>
                            ) : u.role === 'mr' ? (
                              <div>
                                <p className="font-medium text-slate-200">{u.company_name || 'Pharma'}</p>
                                <p className="text-slate-400">{u.division || 'Sales'}</p>
                              </div>
                            ) : (
                              <span className="text-slate-400">System Admin</span>
                            )}
                          </td>

                          <td className="px-6 py-4 text-xs text-slate-300">
                            <p className="flex items-center gap-1">
                              <MapPin className="h-3.5 w-3.5 text-slate-500" />
                              {u.city || 'India'}
                            </p>
                            <p className="text-slate-400">{u.phone}</p>
                          </td>

                          <td className="px-6 py-4">
                            <div className="space-y-1">
                              <button
                                onClick={() => handleToggleVerify(u.id, u.verified)}
                                className={`text-xs font-semibold px-2 py-0.5 rounded border transition-colors ${
                                  u.verified
                                    ? 'bg-emerald-950/60 text-emerald-300 border-emerald-500/30 hover:bg-rose-950/60 hover:text-rose-300 hover:border-rose-500/30'
                                    : 'bg-amber-950/60 text-amber-300 border-amber-500/30 hover:bg-emerald-950/60 hover:text-emerald-300 hover:border-emerald-500/30'
                                }`}
                                title="Click to toggle verification status"
                              >
                                {u.verified ? 'Verified ✓' : 'Unverified !'}
                              </button>

                              {u.role === 'mr' && (
                                <div className="text-[11px]">
                                  <span
                                    className={`font-semibold capitalize ${
                                      u.subscription_plan === 'yearly'
                                        ? 'text-indigo-400'
                                        : u.subscription_plan === 'monthly'
                                        ? 'text-cyan-400'
                                        : 'text-slate-400'
                                    }`}
                                  >
                                    {u.subscription_plan || 'free'} Plan
                                  </span>
                                </div>
                              )}
                            </div>
                          </td>

                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setSelectedUser(u);
                                  setIsDetailModalOpen(true);
                                }}
                                className="h-8 w-8 p-0 text-slate-400 hover:text-white hover:bg-slate-800"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>

                              {u.role === 'mr' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setGrantSubUser(u);
                                    setGrantSubPlan(u.subscription_plan === 'free' ? 'monthly' : u.subscription_plan);
                                    setIsGrantSubOpen(true);
                                  }}
                                  className="h-8 w-8 p-0 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-950/50"
                                  title="Manage Subscription"
                                >
                                  <CreditCard className="h-4 w-4" />
                                </Button>
                              )}

                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setEditingUser(u);
                                  setUserFormData({
                                    email: u.email,
                                    password: '', // blank password unless changing
                                    full_name: u.full_name,
                                    phone: u.phone,
                                    role: u.role,
                                    specialization: u.specialization || '',
                                    clinic_name: u.clinic_name || '',
                                    clinic_address: u.clinic_address || '',
                                    city: u.city || '',
                                    company_name: u.company_name || '',
                                    division: u.division || '',
                                  });
                                  setIsUserFormOpen(true);
                                }}
                                className="h-8 w-8 p-0 text-slate-400 hover:text-white hover:bg-slate-800"
                                title="Edit User"
                              >
                                <Edit3 className="h-4 w-4" />
                              </Button>

                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteUser(u)}
                                className="h-8 w-8 p-0 text-rose-400 hover:text-rose-300 hover:bg-rose-950/50"
                                title="Delete User"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* VIEW 3: APPOINTMENTS OVERVIEW */}
        {activeView === 'appointments' && (
          <div className="space-y-6">
            {/* Filter Bar */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="relative flex-1 w-full md:w-80">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search doctor, MR, date, specialization..."
                  value={apptSearch}
                  onChange={(e) => setApptSearch(e.target.value)}
                  className="pl-9 bg-slate-950 border-slate-800 text-slate-200 focus:border-indigo-500"
                />
              </div>

              <div className="flex items-center bg-slate-950 border border-slate-800 rounded-lg p-1 overflow-x-auto max-w-full">
                {['all', 'pending', 'approved', 'cancelled', 'rescheduled'].map((status) => (
                  <button
                    key={status}
                    onClick={() => setApptStatusFilter(status)}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold uppercase transition-colors whitespace-nowrap ${
                      apptStatusFilter === status
                        ? 'bg-indigo-600 text-white'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            {/* Appointments Table */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-950/80 border-b border-slate-800 text-slate-400 text-xs uppercase font-semibold">
                      <th className="px-6 py-4">Doctor</th>
                      <th className="px-6 py-4">Medical Rep (MR)</th>
                      <th className="px-6 py-4">Date & Time</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4 text-right">Admin Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800 text-sm">
                    {appointments.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                          No appointments found.
                        </td>
                      </tr>
                    ) : (
                      appointments.map((appt) => (
                        <tr key={appt.id} className="hover:bg-slate-800/50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="h-9 w-9 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400 font-bold">
                                Dr
                              </div>
                              <div>
                                <p className="font-semibold text-slate-100">
                                  {appt.doctor_details?.full_name || 'Doctor N/A'}
                                </p>
                                <p className="text-xs text-slate-400">
                                  {appt.doctor_details?.specialization || 'Doctor'} • {appt.doctor_details?.city || 'India'}
                                </p>
                              </div>
                            </div>
                          </td>

                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="h-9 w-9 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 font-bold">
                                MR
                              </div>
                              <div>
                                <p className="font-semibold text-slate-100">
                                  {appt.mr_details?.full_name || 'MR N/A'}
                                </p>
                                <p className="text-xs text-slate-400">
                                  {appt.mr_details?.company_name || 'Pharma'} ({appt.mr_details?.division || 'Rep'})
                                </p>
                              </div>
                            </div>
                          </td>

                          <td className="px-6 py-4">
                            <div className="text-xs">
                              <p className="font-semibold text-slate-200 flex items-center gap-1">
                                <Calendar className="h-3.5 w-3.5 text-slate-400" />
                                {appt.appointment_date}
                              </p>
                              <p className="text-slate-400 flex items-center gap-1 mt-0.5">
                                <Clock className="h-3.5 w-3.5 text-slate-500" />
                                {appt.time_slot}
                              </p>
                            </div>
                          </td>

                          <td className="px-6 py-4">
                            <Badge
                              className={`text-xs font-semibold px-2.5 py-0.5 uppercase border ${
                                appt.status === 'approved'
                                  ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                                  : appt.status === 'pending'
                                  ? 'bg-amber-500/20 text-amber-300 border-amber-500/30'
                                  : appt.status === 'rescheduled'
                                  ? 'bg-blue-500/20 text-blue-300 border-blue-500/30'
                                  : 'bg-rose-500/20 text-rose-300 border-rose-500/30'
                              }`}
                            >
                              {appt.status}
                            </Badge>
                          </td>

                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              {appt.status !== 'approved' && (
                                <Button
                                  size="sm"
                                  onClick={() => handleUpdateApptStatus(appt.id, 'approved')}
                                  className="h-8 bg-emerald-600 hover:bg-emerald-500 text-white text-xs px-2.5"
                                  title="Approve Appointment"
                                >
                                  Approve
                                </Button>
                              )}

                              {appt.status !== 'cancelled' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleUpdateApptStatus(appt.id, 'cancelled')}
                                  className="h-8 border-rose-500/40 text-rose-400 hover:bg-rose-950/50 text-xs px-2.5"
                                  title="Cancel Appointment"
                                >
                                  Cancel
                                </Button>
                              )}

                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setRescheduleAppt(appt);
                                  setRescheduleDate(appt.appointment_date);
                                  setRescheduleTime(appt.time_slot);
                                  setIsRescheduleOpen(true);
                                }}
                                className="h-8 border-blue-500/40 text-blue-400 hover:bg-blue-950/50 text-xs px-2.5"
                                title="Reschedule"
                              >
                                Reschedule
                              </Button>

                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteAppointment(appt.id)}
                                className="h-8 w-8 p-0 text-slate-500 hover:text-rose-400 hover:bg-slate-800"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* VIEW 4: SUBSCRIPTIONS & REVENUE */}
        {activeView === 'subscriptions' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                <p className="text-xs font-semibold text-slate-400 uppercase">Total Revenue Generated</p>
                <h3 className="text-3xl font-bold text-emerald-400 mt-2">₹{stats?.total_revenue_inr || 0}</h3>
                <p className="text-xs text-slate-400 mt-1">Processed via Razorpay gateway</p>
              </div>

              <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                <p className="text-xs font-semibold text-slate-400 uppercase">Completed Transactions</p>
                <h3 className="text-3xl font-bold text-white mt-2">{stats?.completed_transactions || 0}</h3>
                <p className="text-xs text-slate-400 mt-1">Successful payment order logs</p>
              </div>

              <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                <p className="text-xs font-semibold text-slate-400 uppercase">Active Premium MRs</p>
                <h3 className="text-3xl font-bold text-cyan-400 mt-2">{subscriptionsData.paid_subscribers?.length || 0}</h3>
                <p className="text-xs text-slate-400 mt-1">MRs with Monthly or Yearly plans</p>
              </div>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-4">Razorpay Payment Log</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-sm">
                  <thead>
                    <tr className="bg-slate-950/80 border-b border-slate-800 text-slate-400 text-xs uppercase font-semibold">
                      <th className="px-4 py-3">Order ID</th>
                      <th className="px-4 py-3">User</th>
                      <th className="px-4 py-3">Plan</th>
                      <th className="px-4 py-3">Amount</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Created At</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {subscriptionsData.payment_orders?.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-4 py-8 text-center text-slate-500">
                          No payment orders recorded yet.
                        </td>
                      </tr>
                    ) : (
                      subscriptionsData.payment_orders?.map((ord) => (
                        <tr key={ord.order_id} className="hover:bg-slate-800/40">
                          <td className="px-4 py-3 font-mono text-xs text-slate-300">{ord.order_id}</td>
                          <td className="px-4 py-3 text-xs">
                            <p className="font-semibold text-slate-200">{ord.user_details?.full_name || 'MR'}</p>
                            <p className="text-slate-400">{ord.user_details?.email}</p>
                          </td>
                          <td className="px-4 py-3 capitalize text-xs text-slate-300">{ord.plan}</td>
                          <td className="px-4 py-3 font-bold text-emerald-400">₹{(ord.amount || 0) / 100}</td>
                          <td className="px-4 py-3">
                            <Badge
                              className={`text-xs px-2 py-0.5 uppercase border ${
                                ord.status === 'completed'
                                  ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                                  : 'bg-amber-500/20 text-amber-300 border-amber-500/30'
                              }`}
                            >
                              {ord.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-400">
                            {ord.created_at ? new Date(ord.created_at).toLocaleString() : '-'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* VIEW 5: SYSTEM DIAGNOSTICS */}
        {activeView === 'settings' && (
          <div className="space-y-6">
            <h3 className="text-lg font-bold text-white mb-4">System Service Diagnostics</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Database Status */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-slate-200">MongoDB Database</span>
                  <Badge
                    className={`${
                      systemStatus?.database?.connected
                        ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                        : 'bg-rose-500/20 text-rose-400 border-rose-500/30'
                    }`}
                  >
                    {systemStatus?.database?.connected ? 'ONLINE' : 'OFFLINE'}
                  </Badge>
                </div>
                <p className="text-xs text-slate-400">DB Name: {systemStatus?.database?.database_name}</p>
                <p className="text-[11px] font-mono text-slate-500 truncate">{systemStatus?.database?.url}</p>
              </div>

              {/* Razorpay Status */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-slate-200">Razorpay Payment API</span>
                  <Badge
                    className={`${
                      systemStatus?.razorpay?.configured
                        ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                        : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                    }`}
                  >
                    {systemStatus?.razorpay?.configured ? 'ACTIVE (LIVE)' : 'TEST MODE'}
                  </Badge>
                </div>
                <p className="text-xs text-slate-400">Key ID: {systemStatus?.razorpay?.key_id}</p>
              </div>

              {/* Twilio Status */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-slate-200">Twilio SMS Gateway</span>
                  <Badge
                    className={`${
                      systemStatus?.twilio?.configured
                        ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                        : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                    }`}
                  >
                    {systemStatus?.twilio?.configured ? 'ACTIVE' : 'PLACEHOLDER'}
                  </Badge>
                </div>
                <p className="text-xs text-slate-400">Twilio Phone: {systemStatus?.twilio?.phone}</p>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* MODAL 1: VIEW USER PROFILE DETAILS */}
      {isDetailModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-lg w-full p-6 text-slate-100 space-y-5 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start border-b border-slate-800 pb-4">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center font-bold text-lg text-white overflow-hidden">
                  {selectedUser.doctor_photo || selectedUser.mr_photo ? (
                    <img
                      src={selectedUser.doctor_photo || selectedUser.mr_photo}
                      alt={selectedUser.full_name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    selectedUser.full_name?.charAt(0)
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-bold">{selectedUser.full_name}</h3>
                  <p className="text-xs text-slate-400">{selectedUser.email}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsDetailModalOpen(false)}
                className="text-slate-400 hover:text-white"
              >
                ✕
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <p className="text-slate-400">Role</p>
                <p className="font-semibold capitalize text-slate-200">{selectedUser.role}</p>
              </div>
              <div>
                <p className="text-slate-400">Phone</p>
                <p className="font-semibold text-slate-200">{selectedUser.phone}</p>
              </div>
              <div>
                <p className="text-slate-400">City</p>
                <p className="font-semibold text-slate-200">{selectedUser.city || '-'}</p>
              </div>
              <div>
                <p className="text-slate-400">Verification Status</p>
                <p className={`font-semibold ${selectedUser.verified ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {selectedUser.verified ? 'Verified Account' : 'Unverified Account'}
                </p>
              </div>

              {selectedUser.role === 'doctor' && (
                <>
                  <div>
                    <p className="text-slate-400">Specialization</p>
                    <p className="font-semibold text-slate-200">{selectedUser.specialization || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Clinic Name</p>
                    <p className="font-semibold text-slate-200">{selectedUser.clinic_name || '-'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-slate-400">Clinic Address</p>
                    <p className="font-semibold text-slate-200">{selectedUser.clinic_address || '-'}</p>
                  </div>
                </>
              )}

              {selectedUser.role === 'mr' && (
                <>
                  <div>
                    <p className="text-slate-400">Company Name</p>
                    <p className="font-semibold text-slate-200">{selectedUser.company_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Division</p>
                    <p className="font-semibold text-slate-200">{selectedUser.division || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Subscription Plan</p>
                    <p className="font-semibold text-cyan-400 capitalize">{selectedUser.subscription_plan || 'free'}</p>
                  </div>
                </>
              )}
            </div>

            {/* Uploaded Cards / Media */}
            {(selectedUser.visiting_card || selectedUser.mr_visiting_card) && (
              <div className="pt-2 border-t border-slate-800">
                <p className="text-xs font-semibold text-slate-400 mb-2">Uploaded Visiting Card / Brochure</p>
                <div className="rounded-lg overflow-hidden border border-slate-800 bg-slate-950 p-2 max-h-48 flex justify-center">
                  <img
                    src={selectedUser.visiting_card || selectedUser.mr_visiting_card}
                    alt="Visiting Card"
                    className="max-h-44 object-contain rounded"
                  />
                </div>
              </div>
            )}

            <div className="pt-4 flex justify-end">
              <Button
                onClick={() => setIsDetailModalOpen(false)}
                className="bg-slate-800 hover:bg-slate-700 text-slate-200"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 2: ADD / EDIT USER FORM */}
      {isUserFormOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-lg w-full p-6 text-slate-100 space-y-5 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <h3 className="text-lg font-bold">{editingUser ? 'Edit User' : 'Create New User'}</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsUserFormOpen(false)}
                className="text-slate-400 hover:text-white"
              >
                ✕
              </Button>
            </div>

            <form onSubmit={handleSaveUser} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-300">Role</Label>
                  <select
                    value={userFormData.role}
                    onChange={(e) => setUserFormData({ ...userFormData, role: e.target.value })}
                    disabled={!!editingUser}
                    className="w-full mt-1 bg-slate-950 border border-slate-800 rounded-md py-2 px-3 text-xs text-white"
                  >
                    <option value="doctor">Doctor</option>
                    <option value="mr">Medical Representative (MR)</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div>
                  <Label className="text-xs text-slate-300">Full Name</Label>
                  <Input
                    required
                    value={userFormData.full_name}
                    onChange={(e) => setUserFormData({ ...userFormData, full_name: e.target.value })}
                    className="mt-1 bg-slate-950 border-slate-800 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-300">Email Address</Label>
                  <Input
                    type="email"
                    required
                    value={userFormData.email}
                    onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                    className="mt-1 bg-slate-950 border-slate-800 text-white"
                  />
                </div>

                <div>
                  <Label className="text-xs text-slate-300">
                    Password {editingUser && '(Leave blank to keep existing)'}
                  </Label>
                  <Input
                    type="password"
                    required={!editingUser}
                    value={userFormData.password}
                    onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                    className="mt-1 bg-slate-950 border-slate-800 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-300">Phone</Label>
                  <Input
                    required
                    value={userFormData.phone}
                    onChange={(e) => setUserFormData({ ...userFormData, phone: e.target.value })}
                    className="mt-1 bg-slate-950 border-slate-800 text-white"
                  />
                </div>

                <div>
                  <Label className="text-xs text-slate-300">City</Label>
                  <Input
                    value={userFormData.city}
                    onChange={(e) => setUserFormData({ ...userFormData, city: e.target.value })}
                    className="mt-1 bg-slate-950 border-slate-800 text-white"
                  />
                </div>
              </div>

              {userFormData.role === 'doctor' && (
                <div className="space-y-3 pt-2 border-t border-slate-800">
                  <div>
                    <Label className="text-xs text-slate-300">Specialization</Label>
                    <Input
                      value={userFormData.specialization}
                      onChange={(e) => setUserFormData({ ...userFormData, specialization: e.target.value })}
                      className="mt-1 bg-slate-950 border-slate-800 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-300">Clinic Name</Label>
                    <Input
                      value={userFormData.clinic_name}
                      onChange={(e) => setUserFormData({ ...userFormData, clinic_name: e.target.value })}
                      className="mt-1 bg-slate-950 border-slate-800 text-white"
                    />
                  </div>
                </div>
              )}

              {userFormData.role === 'mr' && (
                <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-800">
                  <div>
                    <Label className="text-xs text-slate-300">Company Name</Label>
                    <Input
                      value={userFormData.company_name}
                      onChange={(e) => setUserFormData({ ...userFormData, company_name: e.target.value })}
                      className="mt-1 bg-slate-950 border-slate-800 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-300">Division</Label>
                    <Input
                      value={userFormData.division}
                      onChange={(e) => setUserFormData({ ...userFormData, division: e.target.value })}
                      className="mt-1 bg-slate-950 border-slate-800 text-white"
                    />
                  </div>
                </div>
              )}

              <div className="pt-4 flex justify-end gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setIsUserFormOpen(false)}
                  className="text-slate-400 hover:text-white"
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-500 text-white">
                  {editingUser ? 'Save Changes' : 'Create User'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 3: GRANT SUBSCRIPTION MODAL */}
      {isGrantSubOpen && grantSubUser && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-md w-full p-6 text-slate-100 space-y-5">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <h3 className="text-lg font-bold">Grant Subscription Plan</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsGrantSubOpen(false)}
                className="text-slate-400 hover:text-white"
              >
                ✕
              </Button>
            </div>

            <p className="text-xs text-slate-400">
              Target MR: <span className="font-semibold text-slate-200">{grantSubUser.full_name}</span> ({grantSubUser.email})
            </p>

            <form onSubmit={handleGrantSubscription} className="space-y-4">
              <div>
                <Label className="text-xs text-slate-300">Subscription Tier</Label>
                <select
                  value={grantSubPlan}
                  onChange={(e) => setGrantSubPlan(e.target.value)}
                  className="w-full mt-1 bg-slate-950 border border-slate-800 rounded-md py-2 px-3 text-xs text-white"
                >
                  <option value="free">Free Tier (5 Appointments/month)</option>
                  <option value="monthly">Monthly Premium (Unlimited)</option>
                  <option value="yearly">Yearly Premium (Unlimited)</option>
                </select>
              </div>

              {grantSubPlan !== 'free' && (
                <div>
                  <Label className="text-xs text-slate-300">Duration (Days)</Label>
                  <Input
                    type="number"
                    value={grantSubDays}
                    onChange={(e) => setGrantSubDays(e.target.value)}
                    className="mt-1 bg-slate-950 border-slate-800 text-white"
                  />
                </div>
              )}

              <div className="pt-4 flex justify-end gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setIsGrantSubOpen(false)}
                  className="text-slate-400 hover:text-white"
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-cyan-600 hover:bg-cyan-500 text-white font-semibold">
                  Update Subscription
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 4: RESCHEDULE APPOINTMENT MODAL */}
      {isRescheduleOpen && rescheduleAppt && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-md w-full p-6 text-slate-100 space-y-5">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <h3 className="text-lg font-bold">Reschedule Appointment</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsRescheduleOpen(false)}
                className="text-slate-400 hover:text-white"
              >
                ✕
              </Button>
            </div>

            <form onSubmit={handleRescheduleSubmit} className="space-y-4">
              <div>
                <Label className="text-xs text-slate-300">New Date (YYYY-MM-DD)</Label>
                <Input
                  type="date"
                  required
                  value={rescheduleDate}
                  onChange={(e) => setRescheduleDate(e.target.value)}
                  className="mt-1 bg-slate-950 border-slate-800 text-white"
                />
              </div>

              <div>
                <Label className="text-xs text-slate-300">Time Slot</Label>
                <Input
                  type="text"
                  placeholder="e.g. 10:00 AM - 10:30 AM"
                  required
                  value={rescheduleTime}
                  onChange={(e) => setRescheduleTime(e.target.value)}
                  className="mt-1 bg-slate-950 border-slate-800 text-white"
                />
              </div>

              <div className="pt-4 flex justify-end gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setIsRescheduleOpen(false)}
                  className="text-slate-400 hover:text-white"
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-500 text-white">
                  Save Reschedule
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}